import { Link } from "react-router-dom";
import { ChevronRight, Zap, Layers, Maximize2, Repeat, MoveHorizontal, Grid3X3, Cable, Battery, Thermometer, Shield, Award } from "lucide-react";

const GUIDE_TOPICS = [
  { icon: Zap, title: "Solar panel wattage", desc: "Match wattage to your daily energy needs. Common sizes: 100W for small setups, 200W for moderate use, 400W+ for whole-home." },
  { icon: Maximize2, title: "Solar cell efficiency", desc: "Higher efficiency means more power per square foot. Look for 20%+ efficiency in monocrystalline panels." },
  { icon: Grid3X3, title: "Panel size & roof space", desc: "Measure your available space first. Higher efficiency panels produce more power in less area." },
  { icon: Layers, title: "Rigid vs flexible panels", desc: "Rigid panels are more efficient and durable. Flexible panels work better on curved surfaces.", link: "/flexible-solar-panels" },
  { icon: MoveHorizontal, title: "Portable vs permanent", desc: "Portable panels fold up for camping. Permanent panels mount for year-round production." },
  { icon: Repeat, title: "Mono vs polycrystalline", desc: "Monocrystalline is more efficient and compact. Polycrystalline is cheaper but needs more space.", link: "/monocrystalline-vs-polycrystalline-solar-panels" },
  { icon: Grid3X3, title: "Bifacial solar panels", desc: "Capture light on both sides for 10–30% more energy in the right conditions.", link: "/bifacial-solar-panels" },
  { icon: Cable, title: "Solar panel kits", desc: "Kits include panels, charge controller, and wiring. Great for beginners entering solar." },
  { icon: Zap, title: "Charge controllers", desc: "MPPT controllers are more efficient than PWM. Essential for protecting your batteries." },
  { icon: Cable, title: "MC4 connectors", desc: "Standard connectors used on most solar panels. Ensure compatibility with your system." },
  { icon: Battery, title: "Battery compatibility", desc: "LiFePO4 batteries are safest for solar. Match battery voltage to your panel and controller." },
  { icon: Battery, title: "Solar generator compatibility", desc: "Check input voltage and wattage limits. Most portable generators accept 100–400W of solar." },
  { icon: Thermometer, title: "RV roof mounting", desc: "Use Z-brackets for rigid panels. Clean the roof and seal all drill holes to prevent leaks." },
  { icon: Shield, title: "Weather resistance", desc: "Look for IP67/IP68 ratings. Good panels handle rain, snow, wind, and dust." },
  { icon: Award, title: "Warranty & durability", desc: "Top brands offer 25-year performance warranties. Check for both product and output guarantees." },
];

export default function BuyingGuideSection() {
  return (
    <section id="buying-guide" className="py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4">
        <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-accent-text font-semibold">Buying Guide</span>
        <h2 className="font-heading font-bold text-3xl lg:text-4xl mt-1 mb-3">
          How to Choose the Best High-Efficiency Solar Panel
        </h2>
        <p className="text-muted-foreground mb-10 max-w-2xl">
          Whether you're shopping for an RV, cabin, camping trip, or full off-grid setup,
          these factors will help you pick the right panel.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {GUIDE_TOPICS.map((topic, i) => (
            <div
              key={i}
              className="group bg-card border border-border rounded-xl p-5 hover:border-accent/40 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-accent/20 transition-colors">
                  <topic.icon className="w-4 h-4 text-accent" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-sm mb-1 group-hover:text-accent transition-colors">{topic.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{topic.desc}</p>
                  {topic.link && (
                    <Link to={topic.link} className="inline-flex items-center gap-1 text-xs font-medium text-accent-text mt-2 hover:underline">
                      Read guide <ChevronRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}